import {
  ArrowRight,
  BookOpenCheck,
  CheckCircle2,
  Clock3,
  Filter,
  FolderGit2,
  Save,
  Sparkles,
  Target,
  UserRound,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  generateStudentProjectPlan,
  getGrowthSummary,
  getStudentProjectTemplates,
  saveStudentProjectPlan,
} from '../api.js'
import { useAuth } from '../auth-context.js'
import { getLearningProfile } from '../student-growth-state.js'

const FALLBACK_TEMPLATES = [
  {
    id: 'fallback-campus-survey',
    title: '校园消费问卷分析报告',
    summary: '用问卷收集校园消费习惯，完成数据清洗、图表和结论报告。',
    majorGroup: '经管财会',
    targetGoal: '竞赛/作品集',
    difficulty: '入门',
    durationDays: 7,
    requiredSkills: ['问卷设计', 'Excel 数据清洗', '图表分析', '报告写作'],
    deliverables: ['问卷表', '数据分析表', '8 页以内分析报告'],
    steps: ['明确调研问题', '设计问卷', '收集样本', '清洗数据', '制作图表', '撰写结论', '包装作品集说明'],
    resumeValue: '可展示调研、数据分析和商业结论表达能力。',
    interviewValue: '面试时可以讲清调研设计、样本处理、图表结论和如何用数据支撑建议。',
  },
  {
    id: 'fallback-literature-map',
    title: '文献摘要与研究主题地图',
    summary: '围绕一个课程或论文主题，整理公开文献并形成研究主题地图。',
    majorGroup: '文科/医学/教育',
    targetGoal: '考研/科研',
    difficulty: '入门',
    durationDays: 7,
    requiredSkills: ['文献检索', '摘要整理', '主题归纳'],
    deliverables: ['文献清单', '摘要卡片', '研究主题图'],
    steps: ['确定主题', '检索文献', '筛选 8 篇材料', '整理摘要', '提炼主题', '制作主题地图', '写复盘说明'],
    resumeValue: '适合体现科研入门、论文阅读和结构化表达能力。',
    interviewValue: '面试时可以说明如何筛选资料、归纳主题，并表达自己的研究理解。',
  },
  {
    id: 'fallback-portfolio-homepage',
    title: '个人作品集主页',
    summary: '搭建一个展示课程作品、项目说明和联系方式的个人主页。',
    majorGroup: '计算机/设计',
    targetGoal: '找实习',
    difficulty: '进阶',
    durationDays: 7,
    requiredSkills: ['页面结构', '视觉排版', '作品描述', '部署上线'],
    deliverables: ['作品集页面', '3 个作品条目', '线上访问链接'],
    steps: ['确定页面结构', '整理作品素材', '完成首页草图', '实现页面', '补作品详情', '部署上线', '写简历项目描述'],
    resumeValue: '可直接放进简历，展示项目包装和表达能力。',
    interviewValue: '面试时可以围绕页面结构、作品筛选、上线过程和改进计划展开说明。',
  },
]

const SOURCE_FILTERS = [
  ['', '全部'], ['github', 'GitHub'], ['grabcad', 'GrabCAD'], ['hackster', 'Hackster'],
  ['arduino_project_hub', 'Arduino'], ['kaggle', 'Kaggle'], ['behance', 'Behance'],
]

const SOURCE_LABELS = Object.fromEntries(SOURCE_FILTERS.slice(1))

function sourceMetrics(template) {
  const metrics = template.metrics || {}
  const metadata = template.metadata || {}
  if (template.sourcePlatform === 'github') return [`★ ${metrics.stars ?? 0}`, metadata.language, template.license]
  if (template.sourcePlatform === 'grabcad') return [metadata.software?.[0], metadata.fileTypes?.[0], metrics.downloads != null ? `${metrics.downloads} 下载` : '']
  if (template.sourcePlatform === 'hackster') return [metadata.hardwarePlatform?.[0], metadata.hasCode ? '有代码' : '', metadata.hasSchematic ? '有电路图' : '']
  if (template.sourcePlatform === 'kaggle') return [metadata.datasetOrNotebook, metadata.language, metrics.votes != null ? `${metrics.votes} Votes` : '']
  if (template.sourcePlatform === 'behance') return [metadata.creativeField, metadata.tools?.[0], metrics.appreciations != null ? `${metrics.appreciations} 赞` : '']
  return ['训练任务模板']
}

function safeText(value, fallback = '暂无说明') {
  const text = String(value || '').trim()
  if (!text || text === 'undefined' || text === 'null' || text === 'NaN') return fallback
  return text
}

function safeList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function localPlan(template) {
  const steps = safeList(template.steps)
  return {
    source: 'local-rule',
    templateId: template.id,
    title: `${template.title}：7 天项目计划`,
    summary: '接口暂时不可用时，先使用本地规则生成一个可执行版本。',
    projectGoal: safeText(template.projectGoal, `用 7 天完成“${template.title}”的可展示成果。`),
    analysis: [
      `推荐理由：${safeText(template.recommendationReason, '项目周期短、成果明确，适合先完成再包装进简历。')}`,
      `执行重点：先做最小成果，再补充截图、过程记录和复盘。`,
      `简历落点：${safeText(template.resumeValue, '可作为个人作品集或简历素材。')}`,
    ],
    days: Array.from({ length: 7 }, (_, index) => ({
      day: index + 1,
      title: `第 ${index + 1} 天`,
      task: steps[index] || `推进“${template.title}”的第 ${index + 1} 个小步骤，并记录输出。`,
    })),
    todayTasks: [
      `确定项目主题：${template.title}`,
      '整理 3 条参考资料或样例，记录可借鉴点。',
      '完成第 1 个小输出：项目说明、表格结构、页面草图或报告目录。',
    ],
  }
}

function readStoredProfile() {
  if (typeof window === 'undefined') return {}
  try {
    const parsed = JSON.parse(window.localStorage.getItem('student_profile') || '{}')
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function profilePatch(value = {}) {
  const dailyMinutes = Number(value.dailyMinutes || value.daily_minutes || 60)
  return {
    major: value.major || value.majorGroup || value.major_group || '',
    grade: value.grade || '',
    goal: value.goal || value.careerGoal || value.career_goal || value.targetDirection || '',
    dailyTime: dailyMinutes <= 30 ? '30 分钟' : dailyMinutes <= 60 ? '1 小时' : dailyMinutes <= 120 ? '2 小时' : '半天',
  }
}

function TemplateCard({ template, selected, onSelect }) {
  const metrics = sourceMetrics(template).filter(Boolean)
  return (
    <button
      className={`project-library-card ${selected ? 'is-active' : ''}`}
      type="button"
      onClick={() => onSelect(template)}
    >
      <span><FolderGit2 size={15} /> {SOURCE_LABELS[template.sourcePlatform] || '训练模板'} · {safeText(template.majorGroup, '通用')}</span>
      <strong>{safeText(template.title, '项目模板')}</strong>
      <p>{safeText(template.summary)}</p>
      <div>
        {metrics.slice(0, 3).map((item) => <small key={item}>{item}</small>)}
        <small>{safeText(template.targetGoal, '作品集')}</small>
        <small>{safeText(template.difficulty, '入门')}</small>
        <small>{Number(template.durationDays || 7)} 天</small>
      </div>
    </button>
  )
}

function DetailPanel({ template, plan, profile, onProfileChange, onGenerate, onSave, generating, saving, message }) {
  if (!template) {
    return (
      <aside className="project-library-detail">
        <Sparkles size={22} />
        <h2>选择一个项目模板</h2>
        <p>第一版只展示已审核上线的模板。外部来源素材会先进入后台审核，不直接展示给学生。</p>
      </aside>
    )
  }
  const skills = safeList(template.requiredSkills)
  const deliverables = safeList(template.deliverables)
  const suitableMajors = safeList(template.suitableMajors).length ? safeList(template.suitableMajors) : [safeText(template.majorGroup, '通用')]
  const suitableGrades = safeList(template.suitableGrades).length ? safeList(template.suitableGrades) : ['大一到大四均可按目标调整']
  const steps = safeList(template.steps)
  return (
    <aside className="project-library-detail">
      <span><Target size={16} /> 项目详情</span>
      <h2>{safeText(template.title, '项目模板')}</h2>
      <p>{safeText(template.summary)}</p>
      <div className="project-library-tags">
        <small>{SOURCE_LABELS[template.sourcePlatform] || '训练模板'}</small>
        <small>{safeText(template.majorGroup, '通用')}</small>
        <small>{safeText(template.targetGoal, '作品集')}</small>
        <small>{safeText(template.difficulty, '入门')}</small>
      </div>

      {template.sourceUrl ? (
        <section>
          <h3>真实项目来源</h3>
          <p>{template.authorName ? `作者：${template.authorName} · ` : ''}许可证：{template.license || 'license_unknown'}</p>
          <a className="secondary-button" href={template.sourceUrl} target="_blank" rel="noreferrer">查看原项目 <ArrowRight size={15} /></a>
        </section>
      ) : null}

      <section>
        <h3>为什么推荐给你</h3>
        <p>{safeText(template.recommendationReason, '项目周期短、成果明确，适合从零开始完成并包装为简历经历。')}</p>
      </section>
      <section>
        <h3>适合专业</h3>
        <ul>{suitableMajors.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
      <section>
        <h3>适合年级</h3>
        <ul>{suitableGrades.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
      <section>
        <h3>项目目标</h3>
        <p>{safeText(template.projectGoal, `用 7 天完成“${template.title}”的可展示版本。`)}</p>
      </section>
      <section>
        <h3>最终产出</h3>
        <ul>{deliverables.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
      <section>
        <h3>项目步骤</h3>
        <ol>{steps.slice(0, 7).map((item) => <li key={item}>{item}</li>)}</ol>
      </section>
      <section>
        <h3>技术/能力要求</h3>
        <ul>{skills.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
      <section>
        <h3>简历价值</h3>
        <p>{safeText(template.resumeValue, '可作为个人作品集或简历素材。')}</p>
      </section>
      <section>
        <h3>面试价值</h3>
        <p>{safeText(template.interviewValue, '面试时可以讲清项目背景、你的行动、关键难点、最终成果和下一步优化。')}</p>
      </section>

      <section className="project-library-profile">
        <h3><UserRound size={16} /> 生成前补充你的情况</h3>
        <label>
          <span>用户专业</span>
          <input value={profile.major} onChange={(event) => onProfileChange({ major: event.target.value })} placeholder="例如：市场营销 / 计算机 / 护理" />
        </label>
        <label>
          <span>年级</span>
          <select value={profile.grade} onChange={(event) => onProfileChange({ grade: event.target.value })}>
            <option value="">请选择年级</option>
            <option value="大一">大一</option>
            <option value="大二">大二</option>
            <option value="大三">大三</option>
            <option value="大四">大四</option>
            <option value="研究生">研究生</option>
          </select>
        </label>
        <label>
          <span>目标</span>
          <input value={profile.goal} onChange={(event) => onProfileChange({ goal: event.target.value })} placeholder="例如：找实习 / 竞赛 / 作品集 / 考研" />
        </label>
        <label>
          <span>每天时间</span>
          <select value={profile.dailyTime} onChange={(event) => onProfileChange({ dailyTime: event.target.value })}>
            <option value="30 分钟">30 分钟</option>
            <option value="1 小时">1 小时</option>
            <option value="2 小时">2 小时</option>
            <option value="半天">半天</option>
          </select>
        </label>
      </section>

      <div className="project-library-actions">
        <button className="primary-button" type="button" onClick={onGenerate} disabled={generating}>
          <Clock3 size={16} /> {generating ? '正在生成...' : '开始我的项目'}
        </button>
        {plan ? (
          <button className="secondary-button" type="button" onClick={onSave} disabled={saving}>
            <Save size={16} /> {saving ? '正在加入...' : '加入成长计划'}
          </button>
        ) : null}
      </div>
      {message ? <p className="project-library-message">{message}</p> : null}

      {plan ? (
        <section className="project-library-plan">
          <h3><CheckCircle2 size={17} /> {safeText(plan.title, '7 天项目计划')}</h3>
          <p>{safeText(plan.summary)}</p>
          <section>
            <h3>项目目标</h3>
            <p>{safeText(plan.projectGoal, safeText(template.projectGoal, `用 7 天完成“${template.title}”的可展示成果。`))}</p>
          </section>
          {safeList(plan.analysis).length ? (
            <section>
              <h3>项目分析</h3>
              <ul>{safeList(plan.analysis).map((item) => <li key={item}>{item}</li>)}</ul>
            </section>
          ) : null}
          <div>
            {safeList(plan.todayTasks).map((task, index) => (
              <article key={`${task}-${index}`}>
                <strong>今日任务 {index + 1}</strong>
                <span>{safeText(task, '完成一个项目小任务。')}</span>
              </article>
            ))}
          </div>
          <ol>
            {safeList(plan.days).map((day, index) => (
              <li key={`${day.day || index}-${day.task}`}>
                <strong>Day {day.day || index + 1}：{safeText(day.title, '项目推进')}</strong>
                <span>{safeText(day.task, '完成一个项目小步骤。')}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}
    </aside>
  )
}

function StudentProjectLibraryPage() {
  const { isLoggedIn } = useAuth()
  const navigate = useNavigate()
  const [payload, setPayload] = useState({ items: [], filters: { majorGroups: [], targetGoals: [] } })
  const [selected, setSelected] = useState(null)
  const [majorGroup, setMajorGroup] = useState('')
  const [targetGoal, setTargetGoal] = useState('')
  const [sourcePlatform, setSourcePlatform] = useState('')
  const [profile, setProfile] = useState(() => {
    const stored = readStoredProfile()
    return {
      major: stored.majorGroup || '',
      grade: stored.grade || '',
      goal: stored.goal || '',
      dailyTime: '1 小时',
    }
  })
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [visibleCount, setVisibleCount] = useState(30)

  useEffect(() => {
    if (!isLoggedIn) return undefined
    const controller = new AbortController()
    getGrowthSummary(controller.signal)
      .then((summary) => {
        const savedProfile = getLearningProfile(summary)
        if (savedProfile) setProfile((current) => ({ ...current, ...profilePatch(savedProfile) }))
      })
      .catch(() => {})
    return () => controller.abort()
  }, [isLoggedIn])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setVisibleCount(30)
    getStudentProjectTemplates({ majorGroup, targetGoal, sourcePlatform, profileMajor: profile.major }, controller.signal)
      .then((data) => {
        setPayload(data || { items: [], filters: {} })
        setSelected((current) => current || data?.items?.[0] || null)
      })
      .catch(() => {
        setPayload({
          items: FALLBACK_TEMPLATES,
          filters: {
            majorGroups: ['通用', '计算机/设计', '经管财会', '文科/医学/教育'],
            targetGoals: ['找实习', '作品集', '竞赛/作品集', '考研/科研'],
          },
        })
        setSelected((current) => current || FALLBACK_TEMPLATES[0])
        setMessage('接口暂时不可用，已展示本地基础模板。')
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [majorGroup, targetGoal, sourcePlatform, profile.major])

  const templates = safeList(payload.items)
  const visibleTemplates = templates.slice(0, visibleCount)
  const filters = payload.filters || {}
  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === selected?.id) || selected || templates[0] || null,
    [selected, templates],
  )

  const generatePlan = async () => {
    if (!selectedTemplate) return
    setGenerating(true)
    setMessage('')
    try {
      const data = await generateStudentProjectPlan(selectedTemplate.id, {
        major: profile.major || majorGroup || selectedTemplate.majorGroup,
        grade: profile.grade,
        goal: profile.goal || targetGoal || selectedTemplate.targetGoal,
        dailyTime: profile.dailyTime,
      })
      setPlan(data?.plan || localPlan(selectedTemplate))
    } catch {
      setPlan(localPlan(selectedTemplate))
      setMessage('接口暂时不可用，已使用本地规则生成项目计划。')
    } finally {
      setGenerating(false)
    }
  }

  const savePlan = async () => {
    if (!selectedTemplate || !plan) return
    if (!isLoggedIn) {
      navigate('/login')
      return
    }
    setSaving(true)
    setMessage('')
    try {
      await saveStudentProjectPlan(selectedTemplate.id, { plan })
      setMessage('已加入成长计划，成长中心会显示当前项目进度和今日任务。')
    } catch (error) {
      setMessage(error?.message || '保存失败，请稍后重试。')
    } finally {
      setSaving(false)
    }
  }

  return (
    <main className="page-container project-library-page">
      <section className="project-library-hero">
        <div>
          <span><BookOpenCheck size={18} /> 项目成长计划</span>
          <h1>项目成长计划</h1>
          <p>找到适合你的项目，从零开始完成，并沉淀成简历成果。</p>
        </div>
        <Link className="secondary-button" to="/growth">学生成长中心 <ArrowRight size={16} /></Link>
      </section>

      <section className="project-library-filter">
        <span><Filter size={16} /> 筛选项目模板</span>
        <select value={majorGroup} onChange={(event) => { setMajorGroup(event.target.value); setSelected(null); setPlan(null) }}>
          <option value="">全部专业</option>
          {safeList(filters.majorGroups).map((item) => <option value={item} key={item}>{item}</option>)}
        </select>
        <select value={targetGoal} onChange={(event) => { setTargetGoal(event.target.value); setSelected(null); setPlan(null) }}>
          <option value="">全部目标</option>
          {safeList(filters.targetGoals).map((item) => <option value={item} key={item}>{item}</option>)}
        </select>
      </section>
      {profile.major || profile.goal ? (
        <section className="project-profile-context" aria-label="当前成长画像">
          <strong>已按你的成长画像推荐</strong>
          <span>{profile.major || '未填写专业'} · {profile.goal || '未填写目标'} · 每天 {profile.dailyTime}</span>
          <Link to="/growth/explore/form">调整画像</Link>
        </section>
      ) : null}
      <nav className="project-source-filters" aria-label="项目来源">
        {SOURCE_FILTERS.map(([value, label]) => (
          <button key={label} type="button" className={sourcePlatform === value ? 'is-active' : ''} onClick={() => { setSourcePlatform(value); setSelected(null); setPlan(null) }}>
            {label}
          </button>
        ))}
      </nav>

      <section className="project-library-layout">
        <div className="project-library-list">
          {loading ? <p className="project-library-empty">正在加载项目模板...</p> : null}
          {!loading && templates.length === 0 ? <p className="project-library-empty">暂无符合条件的项目模板。</p> : null}
          {!loading && templates.length > 0 ? (
            <p className="project-library-result-count">
              共找到 {templates.length} 个项目，当前显示 {Math.min(visibleCount, templates.length)} 个
            </p>
          ) : null}
          {visibleTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              selected={selectedTemplate?.id === template.id}
              onSelect={(item) => { setSelected(item); setPlan(null); setMessage('') }}
            />
          ))}
          {visibleCount < templates.length ? (
            <button
              className="secondary-button project-library-load-more"
              type="button"
              onClick={() => setVisibleCount((count) => count + 30)}
            >
              加载更多（剩余 {templates.length - visibleCount} 个）
            </button>
          ) : null}
        </div>
        <DetailPanel
          template={selectedTemplate}
          plan={plan}
          onGenerate={generatePlan}
          onSave={savePlan}
          profile={profile}
          onProfileChange={(patch) => setProfile((current) => ({ ...current, ...patch }))}
          generating={generating}
          saving={saving}
          message={message}
        />
      </section>
    </main>
  )
}

export default StudentProjectLibraryPage
